import { getComponentData, getComponentRating } from 'elements/hardwareSelectorV2/elements/utility'
import {
  ComponentLikeType,
  ComponentLikeTypeWithCatalogData,
  DistributorDataTypeV2,
  HardwareComponentType,
} from 'elements/hardwareSelectorV2/types'
import listQueryParamsFormatter from 'elements/input/listQueryParamsFormatter'

/** Exclude non-catalog keys (e.g. Manual Cost) before sending to componentsv2. */
export const sanitizeDistributorFilterKeys = (distributors?: string[]): string[] =>
  (distributors || []).filter((key) => Boolean(key) && key !== 'component')

export const formatDistributorsApiFilter = (distributors?: string[]): string | undefined => {
  const formatted = listQueryParamsFormatter.format({ value: sanitizeDistributorFilterKeys(distributors) })
  // `format` is typed loosely (may return T); for string[] inputs it JSON-encodes to a string.
  return typeof formatted === 'string' ? formatted : undefined
}

export const pickPreferredDistributorData = (
  ordering: DistributorDataTypeV2[] | undefined,
  preferredDistributors?: string[]
): DistributorDataTypeV2 | undefined => {
  if (!ordering?.length) return undefined
  const preferred = sanitizeDistributorFilterKeys(preferredDistributors)
  if (preferred.length) {
    const preferredSet = new Set(preferred)
    const match = ordering.find(
      (row) => preferredSet.has(row.distributor) && row.short_description && row.short_description.trim() !== ''
    )
    if (match) return match
    const matchAny = ordering.find((row) => preferredSet.has(row.distributor))
    if (matchAny) return matchAny
  }
  return (
    ordering.find((row) => row.short_description && row.short_description.trim() !== '') ||
    ordering.find((row) => row.title && row.title.trim() !== '') ||
    ordering[0]
  )
}

export const formatTypeaheadProductTitle = (
  record: ComponentLikeType,
  distributorData?: DistributorDataTypeV2
): string => {
  return (
    distributorData?.title?.trim() ||
    distributorData?.short_description?.trim() ||
    record.name?.trim() ||
    record.manufacturer_name?.trim() ||
    record.code
  )
}

export const formatTypeaheadRatingUnit = (componentType?: string): string | undefined => {
  if (componentType === 'module') return 'W'
  if (componentType === 'inverter') return 'kW'
  if (componentType === 'battery') return 'kWh'
  return undefined
}

export const formatTypeaheadRatingLabel = (record: ComponentLikeTypeWithCatalogData): string | undefined => {
  const componentData = getComponentData(record as HardwareComponentType)
  const rating = getComponentRating(record.type, componentData)
  if (rating !== '-' && typeof rating === 'object' && rating.value !== '' && rating.value !== '-') {
    return `${rating.value}${rating.suffix ? ` ${rating.suffix}` : ''}`
  }
  if (record.rating != null && record.rating !== 0) {
    const unit = formatTypeaheadRatingUnit(record.type)
    return unit ? `${record.rating} ${unit}` : String(record.rating)
  }
  return undefined
}

export const getTypeaheadProductMetaParts = (
  record: ComponentLikeTypeWithCatalogData,
  distributorData?: DistributorDataTypeV2
): string[] => {
  const parts: string[] = []
  const supplier = distributorData?.distributor_name?.trim()
  if (supplier) parts.push(supplier)
  if (record.code) parts.push(record.code)
  const sku =
    distributorData?.distributor_sku?.trim() ||
    distributorData?.distributor_variant_id?.trim() ||
    distributorData?.variant_id?.trim()
  if (sku) parts.push(`SKU ${sku}`)
  const ratingLabel = formatTypeaheadRatingLabel(record)
  if (ratingLabel) parts.push(ratingLabel)
  return parts
}

export const formatTypeaheadProductMeta = (
  record: ComponentLikeTypeWithCatalogData,
  distributorData?: DistributorDataTypeV2
): string => getTypeaheadProductMetaParts(record, distributorData).join(' · ')
